import { Component, Input, input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StrategicPillarService } from '../../services/strategic-pillar.service';
import { Pillar, Strategy } from '../../interfaces/models/strategy.model';
import { goalColumns, pillarColumns } from '../../static-data/table.data';
import { Column } from '../../interfaces/models/Column.model';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { FormGroup, FormsModule } from '@angular/forms';
import { GoalService } from '../../services/goal.service';
import { Goal, GoalWeightSummary } from '../../interfaces/models/goal.model';
import { DropdownModule } from 'primeng/dropdown';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { ConfirmPopup, ConfirmPopupModule } from 'primeng/confirmpopup';
import { TranslateModule } from '@ngx-translate/core';
import { DirectionService } from '../../services/direction.service';
import { Subject, takeUntil } from 'rxjs';
import { CustomAblePipe } from '../../pipes/able.pipe';
import { AuthService } from '../../services/auth.service';
import { authUser } from '../../interfaces';
import { filterActive } from '../../utils/filter-active';
@Component({
  selector: 'app-strategic-pillar',
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    RouterLink,
    ButtonModule,
    TooltipModule,
    InputTextModule,
    InputTextareaModule,
    DialogModule,
    FormsModule,
    DropdownModule,
    ConfirmPopupModule,
    TranslateModule,
    CustomAblePipe

  ],
  providers: [ConfirmationService],
  templateUrl: './strategic-pillar.component.html',
  styleUrl: './strategic-pillar.component.scss'
})
export class StrategicPillarComponent implements OnInit, OnDestroy {
  @Input() ShowGoals = true;
  @Input() ShowPillarsForm = true;
  @ViewChild(ConfirmPopup) confirmPopup!: ConfirmPopup;
  @ViewChild('pillarForm') pillarForm!: FormGroup;
  @ViewChild('goalForm') goalForm!: FormGroup;
  Pillars = [] as Pillar[];
  goals = [] as Goal[];
  serverWeightSummary: GoalWeightSummary | null = null;
  Pillarsoptions = [] as { name: string, id: number }[];
  isVisiblePillarModal = false;
  isVisibleGoalModal = false;
  pillarColumns = pillarColumns as Column[];
  goalColumns = goalColumns as Column[];
  @Input() strategyId!: number;
  selectedUpdatePillarId = 0;
  selectedUpdateGoalId = 0;
  selectedPillar = {} as Pillar;
  selectedGoal = {} as Goal;
  strategy!: Strategy;
  dir!: 'rtl' | 'ltr';
  user!: authUser;
  private unsubscribe$ = new Subject<void>();
  constructor(private strategicPillarService: StrategicPillarService,
    private goalService: GoalService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private confirmationService: ConfirmationService,
    private directionService: DirectionService,
    private authService: AuthService
  ) {
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  ngOnInit(): void {
    this.authService.authBufferSub$.pipe(takeUntil(this.unsubscribe$)).subscribe(user => {
      this.user = user as authUser;
    })
    this.route.data.pipe(takeUntil(this.unsubscribe$)).subscribe(({ viewBag }) => {
      const strategy = viewBag.strategy?.data?.strategy;
      if (!strategy?.id) {
        this.toastr.error(this.dir === 'rtl' ? 'الاستراتيجية غير موجودة' : 'Strategy not found');
        return;
      }
      this.strategyId = strategy.id;
      this.strategy = strategy;
      this.Pillars = filterActive(viewBag.pillars);
      this.Pillarsoptions = this.Pillars.map(x => ({ name: x.name, id: +x.id }));
      this.goals = filterActive(viewBag.goals ?? []);
      this.serverWeightSummary = viewBag.weight_summary ?? null;
    })
    this.directionService.direction$.pipe(takeUntil(this.unsubscribe$)).subscribe(dir => {
      this.dir = dir;
    })
  }

  showPillarModal() {
    this.isVisiblePillarModal = !this.isVisiblePillarModal;
  }
  showUpdatePillarModal(pillarId: number) {
    this.selectedUpdatePillarId = pillarId;
    this.selectedPillar = { ...this.Pillars.find(x => x.id == pillarId) as Pillar };
    this.isVisiblePillarModal = !this.isVisiblePillarModal;
  }

  showUpdateGoalModal(goalId: number) {
    this.selectedUpdateGoalId = goalId;
    this.selectedGoal = { ...this.goals.find(x => x.id == goalId) } as Goal;
    this.isVisibleGoalModal = !this.isVisibleGoalModal;
  }
  showGoalModal() {
    this.selectedUpdateGoalId = 0;
    this.selectedGoal = {} as Goal;
    this.refreshGoalsFromServer(() => {
      this.isVisibleGoalModal = true;
    });
  }

  private refreshGoalsFromServer(onComplete?: () => void) {
    if (!this.strategyId) {
      onComplete?.();
      return;
    }
    this.goalService.getListGoals(+this.strategyId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response) => {
        this.goals = filterActive(response.goals ?? []);
        this.serverWeightSummary = response.weight_summary ?? null;
        onComplete?.();
      },
      error: () => onComplete?.()
    });
  }

  get totalGoalsWeight(): number {
    const goalsToSum = this.selectedUpdateGoalId > 0
      ? this.goals.filter(g => g.id !== this.selectedUpdateGoalId)
      : this.goals;
    const clientTotal = goalsToSum.reduce((sum, g) => sum + (+g.weight || 0), 0);
    if (this.selectedUpdateGoalId === 0 && this.serverWeightSummary) {
      return +this.serverWeightSummary.total_weight || clientTotal;
    }
    return clientTotal;
  }

  get remainingGoalWeight(): number {
    if (this.serverWeightSummary && this.selectedUpdateGoalId === 0) {
      return Math.max(0, +this.serverWeightSummary.remaining_weight || 0);
    }
    return Math.max(0, 100 - this.totalGoalsWeight);
  }

  private validateGoalWeight(weight: number): string | null {
    if (!weight || weight <= 0) {
      return this.dir === 'rtl' ? 'يجب أن يكون الوزن أكبر من 0' : 'Weight must be greater than 0';
    }
    if (weight > 100) {
      return this.dir === 'rtl' ? 'لا يمكن أن يتجاوز الوزن 100٪' : 'Weight cannot exceed 100%';
    }
    if (this.totalGoalsWeight + weight > 100) {
      return this.dir === 'rtl'
        ? `لا يمكن أن يتجاوز مجموع أوزان الأهداف 100٪. المستخدم حالياً: ${this.totalGoalsWeight}٪، المتبقي: ${this.remainingGoalWeight}٪.`
        : `Goal weights cannot exceed 100%. Currently used: ${this.totalGoalsWeight}%, remaining: ${this.remainingGoalWeight}%.`;
    }
    return null;
  }
  submitPillar() {
    //create 
    if (this.selectedUpdatePillarId == 0 && this.pillarForm.valid) {
      this.strategicPillarService.createPillars({
        ...this.pillarForm.value
      }, +this.strategyId).pipe(takeUntil(this.unsubscribe$)).subscribe((response: any) => {
        this.toastr.success(this.dir == 'rtl' ? 'تم إنشاء المحور' : 'Pillar created successfully');
        this.Pillars = [...this.Pillars, { ...this.pillarForm.value, id: response["data"]["entity"]["id"] } as Pillar]
        this.Pillarsoptions = this.Pillars.map(x => ({ name: x.name, id: +x.id }));
        this.pillarForm.reset({});
        this.isVisiblePillarModal = false;
      })
      // update
    } else if (this.selectedUpdatePillarId > 0) {
      this.strategicPillarService.updatePillars(this.selectedPillar, +this.strategyId, this.selectedUpdatePillarId).pipe(takeUntil(this.unsubscribe$)).subscribe((response: any) => {
        this.toastr.success(this.dir == 'rtl' ? 'تم التعديل' : 'Pillar updated successfully');
        this.Pillars = this.Pillars.map(x => {
          if (x.id == this.selectedUpdatePillarId) {
            return this.selectedPillar;
          } else {
            return x;
          }
        })
        this.Pillarsoptions = this.Pillars.map(x => ({ name: x.name, id: +x.id }));
        this.selectedUpdatePillarId = 0;
        this.selectedPillar = {} as Pillar;
        this.isVisiblePillarModal = false;

      })
    }
  }
  confirmPillarDelete(event: Event, id: number) {
    const target = event.target as HTMLElement;
    this.confirmationService.confirm({
      target: target,
      message: this.dir == 'rtl' ? 'هل تريد تأكيد المسح؟' : 'Do you want to confirm the deletion?',
      accept: () => {
        this.strategicPillarService.deletePillars(+this.strategyId, id).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
          this.Pillars = this.Pillars.filter(pillar => pillar.id != id);
          this.Pillarsoptions = this.Pillars.map(x => ({ name: x.name, id: +x.id }));
          this.toastr.success(this.dir == 'rtl' ? 'تمت المسح ' : 'The pillar has been deleted successfully.', this.dir == 'rtl' ? 'عملية ناجحة' : 'Success');
        }, (error) => {
          this.toastr.error(error['error']['error']['message']);

        })
      },
      reject: () => {
        this.toastr.info(this.dir == 'rtl' ? "تم الالغاء" : "The deletion has been canceled.", this.dir == 'rtl' ? 'الغاء' : 'Canceled');
      }
    });
  }
  confirmGoalDelete(event: Event, id: number) {
    const target = event.target as HTMLElement;
    this.confirmationService.confirm({
      target: target,
      message: this.dir == 'rtl' ? 'هل تريد تأكيد المسح؟' : 'Do you want to confirm the deletion?',
      accept: () => {
        this.goalService.deleteGoal(+this.strategyId, id).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
          this.refreshGoalsFromServer();
          this.toastr.success(this.dir == 'rtl' ? 'تم المسح ' : 'The goal has been deleted successfully.', this.dir == 'rtl' ? 'عملية ناجحة' : 'Success');
        }, (err) => {
          this.toastr.success(err.error.message);
        })
      },
      reject: () => {
        this.toastr.info(this.dir == 'rtl' ? "تم الالغاء" : "The deletion has been canceled.", this.dir == 'rtl' ? 'الغاء' : 'Canceled');
      }
    });
  }
  accept() {
    this.confirmPopup.accept();
  }
  reject() {
    this.confirmPopup.reject();
  }

  private getApiErrorMessage(error: any, fallbackAr: string, fallbackEn: string): string {
    if (error?.status === 403) {
      return error?.error?.error?.message
        ?? error?.error?.message
        ?? (this.dir === 'rtl'
          ? 'ليس لديك صلاحية لهذه العملية. يتطلب دور مدير الاستراتيجية.'
          : 'You do not have permission. Strategy admin role is required.');
    }
    return error?.error?.error?.message
      ?? error?.error?.message
      ?? (this.dir === 'rtl' ? fallbackAr : fallbackEn);
  }

  submitGoal() {
    if (this.selectedUpdateGoalId == 0 && this.goalForm.valid) {
      const payload = {
        name: this.goalForm.value['name'],
        code: this.goalForm.value['code'],
        description: this.goalForm.value['description'],
        pillar_id: +this.goalForm.value['pillar_id'],
        weight: +this.goalForm.value['weight'],
      };
      if (!payload.pillar_id || Number.isNaN(payload.pillar_id)) {
        this.toastr.error(this.dir === 'rtl' ? 'يرجى اختيار المحور' : 'Please select a pillar');
        return;
      }
      const weightError = this.validateGoalWeight(payload.weight);
      if (weightError) {
        this.toastr.error(weightError);
        return;
      }
      this.goalService.createGoal(payload, +this.strategyId).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (response: any) => {
        this.toastr.success(this.dir == 'rtl' ? ' تمت الاضافة ' : 'Goal added successfully');
        this.refreshGoalsFromServer();
        this.goalForm.reset({});
        this.isVisibleGoalModal = false;
        },
        error: (error) => {
          this.refreshGoalsFromServer();
          this.toastr.error(this.getApiErrorMessage(error, 'فشل إضافة الهدف', 'Failed to add goal'));
        }
      })
    } else if (this.selectedUpdateGoalId > 0) {
      const { pillar_id, name, icon, description, code, weight } = this.selectedGoal;
      const finalSelectedGoal = {
        pillar_id: +pillar_id,
        name,
        icon,
        description,
        code,
        weight: +weight,
      }
      const weightError = this.validateGoalWeight(finalSelectedGoal.weight);
      if (weightError) {
        this.toastr.error(weightError);
        return;
      }
      this.goalService.updateGoals(finalSelectedGoal, +this.strategyId, this.selectedUpdateGoalId).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: () => {
        this.toastr.success(this.dir == 'rtl' ? 'تم التعديل' : 'Goal updated successfully');
        this.refreshGoalsFromServer();
        this.selectedUpdateGoalId = 0;
        this.selectedGoal = {} as Goal;
        this.isVisibleGoalModal = false;
        },
        error: (error) => {
          this.refreshGoalsFromServer();
          this.toastr.error(this.getApiErrorMessage(error, 'فشل تعديل الهدف', 'Failed to update goal'));
        }
      })
    }
  }
  closePillarModal() {
    this.isVisiblePillarModal = false;
    this.selectedUpdatePillarId = 0;
    this.selectedPillar = {} as Pillar;
  }
  closeGoalModal(isVisibleGoalModal = false) {
    this.selectedGoal = {} as Goal;
    this.selectedUpdateGoalId = 0;
    this.isVisibleGoalModal = false;
  }

}
